"use client"

import { useState } from 'react'
import { Clock, AlertTriangle, CheckCircle2, CreditCard } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useStore } from '@/lib/store'
import { subscriptionState } from '@/lib/subscription'
import { isPlayInstall } from '@/lib/channel'
import { PayDialog } from '@/components/billing/pay-panel'
import { useT } from '@/lib/i18n'

/**
 * 097 — the one line above every page that says the subscription needs
 * something: a running trial, its three days of grace, the last week of a
 * paid period, an overdue one — or that the owner's „I transferred" is
 * waiting for the confirmation. Its button opens the payment panel.
 *
 * The owner's alone: a cashier cannot pay and should not be told the shop is
 * late. Nothing shows for a paid shop with more than a week left.
 */
export function BillingBanner() {
  const t = useT()
  const org = useStore((s) => s.currentOrg)
  // 098: support in a shop is not its owner — the shop's bill is not his to pay
  const isOwner = useStore((s) => s.userRole === 'owner' && !s.supportSession)
  const [open, setOpen] = useState(false)

  if (!org || !isOwner) return null
  const st = subscriptionState(org)

  let tone: 'info' | 'warn' | 'bad' | 'done'
  let text: string
  if (org.pay_claim_at && st.kind !== 'paid') {
    tone = 'done'
    text = t('bill.banner.1')
  } else if (st.kind === 'trial') {
    tone = 'info'
    text = t('bill.banner.2', { daysLeft: st.daysLeft })
  } else if (st.kind === 'trial-grace') {
    tone = 'bad'
    text = t('bill.banner.3', { lockInDays: st.lockInDays })
  } else if (st.kind === 'due-soon') {
    tone = 'warn'
    text = st.daysLeft === 0 ? t('bill.banner.4') : t('bill.banner.5', { daysLeft: st.daysLeft })
  } else if (st.kind === 'overdue') {
    tone = 'bad'
    text = t('bill.banner.6', { daysOver: st.daysOver })
  } else {
    return null
  }

  const Icon = tone === 'done' ? CheckCircle2 : tone === 'info' ? Clock : AlertTriangle
  // The Google Play build says where the shop stands, never how to pay
  // (lib/channel.ts — Play's payments policy).
  const play = isPlayInstall()
  return (
    <>
      <div
        className={cn(
          'shrink-0 rounded-2xl border px-3 py-2 sm:px-4 flex items-center gap-3',
          tone === 'info' && 'bg-primary/[0.07] border-primary/20',
          tone === 'warn' && 'bg-amber-500/10 border-amber-500/30',
          tone === 'bad' && 'bg-red-500/10 border-red-500/25',
          tone === 'done' && 'bg-emerald-500/10 border-emerald-500/25',
        )}
      >
        <span
          className={cn(
            'size-8 rounded-xl flex items-center justify-center shrink-0 text-white',
            tone === 'info' && 'bg-primary',
            tone === 'warn' && 'bg-amber-500',
            tone === 'bad' && 'bg-red-500',
            tone === 'done' && 'bg-emerald-600',
          )}
        >
          <Icon className="size-4" />
        </span>
        <p
          className={cn(
            'flex-1 min-w-0 text-[13px] sm:text-sm font-bold leading-snug',
            tone === 'info' && 'text-foreground',
            tone === 'warn' && 'text-amber-800 dark:text-amber-300',
            tone === 'bad' && 'text-red-700 dark:text-red-300',
            tone === 'done' && 'text-emerald-800 dark:text-emerald-300',
          )}
        >
          {text}
        </p>
        {!play && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={cn(
              'shrink-0 h-9 px-3.5 rounded-xl text-[13px] font-bold flex items-center gap-1.5 transition-colors',
              tone === 'done'
                ? 'border border-border bg-card hover:bg-muted/60 text-foreground'
                : 'bg-primary text-primary-foreground hover:bg-primary/90',
            )}
          >
            <CreditCard className="size-4" />
            {tone === 'done' ? t('bill.banner.7') : t('bill.banner.8')}
          </button>
        )}
      </div>
      {!play && <PayDialog open={open} onOpenChange={setOpen} />}
    </>
  )
}
